import {Component, computed, input, output, signal} from '@angular/core';
import {AppNumberPipe} from '@frontend/shared';
import {CommonModule} from '@angular/common';
import {PanelModule} from 'primeng/panel';
import {EvaluationMatrixComponent} from '../evaluation-matrix/evaluation-matrix.component';
import {
  UserEvaluationRequest,
  ParticipatingToolResponse,
  EvaluationUpsertRequest,
} from '@frontend/shared-api';
import {HrTool} from '@frontend/directus';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {
  getMaxScore,
  getScoreFromRating,
  getThresholdScore,
} from '../../../../../../../../shared/utils/rfd-evaluation-score.utils';
import {EnrichedEvaluationDataResponse} from '../../evaluation-models';

@Component({
  selector: 'app-provider-evaluation-card',
  imports: [
    AppNumberPipe,
    CommonModule,
    PanelModule,
    EvaluationMatrixComponent,
    ProjectStepSaveButtonComponent,
  ],
  templateUrl: './provider-evaluation-card.component.html',
})
export class ProviderEvaluationCardComponent {
  readonly tool = input.required<ParticipatingToolResponse>();
  readonly data = input.required<EnrichedEvaluationDataResponse>();
  readonly selectedUserId = input<number | null>(null);
  readonly averageMode = input(false);
  readonly averageUserIds = input<number[]>([]);
  readonly currentUserId = input<number | null | undefined>(null);
  readonly readOnly = input(false);
  readonly hrToolsMap = input<Map<string, HrTool>>(new Map<string, HrTool>());
  readonly isExponentialScale = input(false);

  readonly saveEvaluations = output<EvaluationUpsertRequest>();
  readonly commentOpen = output<{agendaItemId: number; toolId: number}>();

  protected changedRatings = signal<UserEvaluationRequest[]>([]);

  protected readonly hrTool = computed(() => this.hrToolsMap().get(this.tool().hrToolDirectusId));
  protected readonly providerName = computed(
    () => this.hrTool()?.toolProvider?.name ?? this.tool().hrToolDirectusId,
  );
  protected readonly toolName = computed(() => this.hrTool()?.name ?? this.tool().hrToolDirectusId);

  protected readonly overallScore = computed(() => {
    const data = this.data();
    const toolId = this.tool().id;
    if (!data || !toolId) {
      return {overall: 0, percent: 0};
    }

    const maxScore = getMaxScore(this.isExponentialScale());
    const matrixRowById = new Map<
      number,
      {demonstrationAgendaItemId?: number; subcriteriaDirectusId?: string}
    >();
    for (const row of data.matrixRows ?? []) {
      if (row.id == null) continue;
      matrixRowById.set(row.id, row);
    }

    const ratingsMap = new Map<string, number>();
    const eligibleUserIds = new Set<number>(this.averageUserIds() ?? []);
    const sums = new Map<string, {sum: number; count: number}>();

    (data.userEvaluations ?? []).forEach((e) => {
      const participatingToolId = e.participatingToolId;
      if (participatingToolId == null || participatingToolId !== toolId) return;

      const userId = e.userId;
      if (userId == null || !eligibleUserIds.has(userId)) return;
      if (e.rating == null || e.rating === -1) return;

      const matrixId = e.matrixId;
      if (matrixId == null) return;

      const matrixRow = matrixRowById.get(matrixId);
      if (!matrixRow) return;

      const agendaItemId = matrixRow.demonstrationAgendaItemId;
      const subcriteriaId = matrixRow.subcriteriaDirectusId;
      if (agendaItemId == null || !subcriteriaId) return;

      const score = getScoreFromRating(e.rating, this.isExponentialScale());
      if (score == null) return;

      const key = `${agendaItemId}-${subcriteriaId}`;
      const current = sums.get(key) ?? {sum: 0, count: 0};
      sums.set(key, {sum: current.sum + score, count: current.count + 1});
    });

    sums.forEach((v, key) => {
      if (v.count > 0) {
        ratingsMap.set(key, v.sum / v.count);
      }
    });

    const subcriteriaScores = new Map<string, number>();
    (data.subcriteria ?? []).forEach((subcriteria) => {
      const ratings: number[] = [];
      (data.agendaItems ?? []).forEach((item) => {
        if (item.id == null) return;
        const key = `${item.id}-${subcriteria.id}`;
        const score = ratingsMap.get(key);
        if (score != null) {
          ratings.push(score);
        }
      });
      if (ratings.length > 0) {
        const sum = ratings.reduce((acc, val) => acc + val, 0);
        subcriteriaScores.set(subcriteria.id, sum / ratings.length);
      }
    });

    const overall = (data.subcriteria ?? []).reduce((acc, subcriteria) => {
      const score = subcriteriaScores.get(subcriteria.id) ?? 0;
      const weight = subcriteria.weight ?? 0;
      return acc + score * weight;
    }, 0);

    return {
      overall,
      percent: (overall / maxScore) * 100,
    };
  });

  protected getThresholdScore(starValue: number): number {
    return getThresholdScore(starValue, this.isExponentialScale());
  }

  onRatingChange(req: UserEvaluationRequest) {
    const current = this.changedRatings();
    const index = current.findIndex((r) => r.matrixId === req.matrixId);
    if (index >= 0) {
      const updated = [...current];
      updated[index] = req;
      this.changedRatings.set(updated);
    } else {
      this.changedRatings.set([...current, req]);
    }
  }

  save() {
    if (this.readOnly()) return;
    const evaluations = this.changedRatings();
    if (evaluations.length === 0) return;

    this.saveEvaluations.emit({evaluations});
    this.changedRatings.set([]);
  }

  onCommentOpen(agendaItemId: number) {
    this.commentOpen.emit({
      agendaItemId,
      toolId: this.tool().id,
    });
  }
}
